import { cn } from '@/lib/cn';
import {
  Tooltip,
  TooltipArrow,
  TooltipContent,
  TooltipTrigger,
} from '@heidi/design-system/tooltip';
import { type ReactNode } from 'react';
import { FormattedMessage } from 'react-intl';

import { offlineGatingMessages } from './offline-gating-messages';

/**
 * Wraps a gated control in a tooltip that explains why it is unavailable, and
 * renders no tooltip content when it isn't gated. Consolidates the identical
 * Tooltip/inline-flex-span/gated-content boilerplate these controls share.
 *
 * `className` is applied to the trigger span — pass `"w-full"` when wrapping a
 * full-width button so the span doesn't shrink it to content width. Several
 * callers also use it for layout the span owns (`hidden sm:inline-flex`, a
 * bordered box), which is why the span is rendered on both branches: dropping
 * it while ungated would change their online layout.
 *
 * `content` overrides the default "Not available offline" copy for gates that
 * are not about connectivity — e.g. an offline session that already holds saved
 * audio, where the reason and the suggested next step are different.
 *
 * `role` is for adopters that wrap a control from *inside* an ARIA container
 * with required children — a `role="menuitem"` inside a `role="menu"`. The span
 * would sit between the two and break `aria-required-children`, so those call
 * sites pass `"group"`, which `menu` permits as a child and which in turn
 * permits `menuitem`. Wrapping a menu, popover or toolbar button from the
 * outside needs no role.
 *
 * `"none"`/`"presentation"` would read better but cannot be used here: this span
 * is always a tooltip trigger, so Radix puts `aria-describedby` on it whenever
 * the tooltip is open, and ARIA ignores a presentational role on an element
 * carrying a global ARIA attribute. The flattening would lapse in exactly the
 * state the tooltip exists for. A container role holds in both states.
 *
 * Undo/redo intentionally do NOT use this wrapper: their buttons sit as direct
 * `ButtonGroup` children (no span wrapper) so its border/radius merging works,
 * and their tooltip has an extra "Nothing to undo/redo" branch.
 */
export const OfflineGatedTooltip = ({
  isOfflineGated,
  children,
  className,
  content,
  role,
}: {
  isOfflineGated: boolean;
  children: ReactNode;
  className?: string;
  content?: ReactNode;
  role?: 'group';
}) => {
  const trigger = (
    <span
      role={role}
      className={cn('inline-flex', className)}
      tabIndex={isOfflineGated && !role ? 0 : undefined}
    >
      {children}
    </span>
  );

  if (!isOfflineGated) {
    return trigger;
  }

  return (
    <Tooltip>
      <TooltipTrigger asChild>{trigger}</TooltipTrigger>
      <TooltipContent side="top" align="center" className="max-w-xs">
        {content ?? <FormattedMessage {...offlineGatingMessages.notAvailableOffline} />}
        <TooltipArrow />
      </TooltipContent>
    </Tooltip>
  );
};

// Greys out customise controls on an unsynced offline session, explaining why on hover.
// The inner wrapper drops pointer events so a click never reaches the controls (a
// disabled scribe card would open the upgrade modal) and the hover hits the trigger.
export const AwaitingSyncTooltip = ({
  isAwaitingSync,
  children,
  className = 'w-full',
}: {
  isAwaitingSync: boolean;
  children: ReactNode;
  className?: string;
}) => (
  <OfflineGatedTooltip
    isOfflineGated={isAwaitingSync}
    content={<FormattedMessage {...offlineGatingMessages.availableOnceSynced} />}
    className={className}
  >
    <div
      className={cn(className, isAwaitingSync && 'pointer-events-none')}
      data-testid={isAwaitingSync ? 'awaiting-sync-controls' : undefined}
    >
      {children}
    </div>
  </OfflineGatedTooltip>
);
